/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

// src/components/ContextMenu/AracContextMenu.js
import { Box, Text, Flex } from "@chakra-ui/react";
import { useState, useRef } from "react";
import { getContextMenuPosition } from "utils/contextMenuPosition";

function AracContextMenu({
  position,
  data,
  userRole,
  userBayiTipi,
  onClose,
  onOpenImeiDegistir,
  onOpenImeiGecmisi,
  onOpenPlakaDegistir,
  onOpenPlakaGecmisi,
  onOpenSimGecmisi,
  onGatherSim,
  onOpenNotEkle,
  onOpenIadeAl,
  onOpenMontajBaslat,
  onOpenAksesuarIslemleri,
  onOpenUetdsKonumBildir,
  onUetdsAriza,
}) {
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
  const timeoutRef = useRef(null);

  if (!position) return null;
  const menuPosition = getContextMenuPosition(position, { menuWidth: 240, menuHeight: 400 });

  // Yetkili bayi kontrolü: yetkili bayi SIM işlemlerini göremez
  const isYetkiliBayi = userRole === "bayi" && userBayiTipi === "yetkili_bayi";

  const handleMouseLeave = () => {
    if (!hoveredSubmenu) {
      timeoutRef.current = setTimeout(() => onClose(), 100);
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return (
    <>
      <Box
        position="fixed"
        top={`${menuPosition.y}px`}
        left={`${menuPosition.x}px`}
        bg="white"
        borderRadius="lg"
        shadow="0 4px 12px rgba(0, 0, 0, 0.15)"
        zIndex={2000}
        onMouseLeave={handleMouseLeave}
        onMouseEnter={handleMouseEnter}
        minW="180px"
        fontSize="11px"
        py={1}
        border="1px solid"
        borderColor="gray.200"
      >
        {/* SIM Kart İşlemleri - Yetkili bayi göremez */}
        {!isYetkiliBayi && (
          <SubmenuItem
            label="SIM Kart İşlemleri"
            onMouseEnter={() => setHoveredSubmenu("sim")}
            onMouseLeave={() => setHoveredSubmenu(null)}
          >
            {hoveredSubmenu === "sim" && (
              <Submenu
                items={[
                  { label: "SIM No Getir", onClick: () => { onGatherSim?.(data); onClose(); } },
                  { label: "SIM Geçmişi", onClick: () => { onOpenSimGecmisi?.(data); onClose(); } },
                ]}
                onClose={onClose}
                onMouseEnter={() => setHoveredSubmenu("sim")}
                onMouseLeave={() => setHoveredSubmenu(null)}
              />
            )}
          </SubmenuItem>
        )}

        {/* IMEI İşlemleri */}
        <SubmenuItem
          label="IMEI İşlemleri"
          onMouseEnter={() => setHoveredSubmenu("imei")}
          onMouseLeave={() => setHoveredSubmenu(null)}
        >
          {hoveredSubmenu === "imei" && (
            <Submenu
              items={[
                { label: "IMEI Geçmişi", onClick: () => { onOpenImeiGecmisi?.(data); onClose(); } },
                { label: "IMEI Değiştir", onClick: () => { onOpenImeiDegistir?.(data); onClose(); } },
              ]}
              onClose={onClose}
              onMouseEnter={() => setHoveredSubmenu("imei")}
              onMouseLeave={() => setHoveredSubmenu(null)}
            />
          )}
        </SubmenuItem>

        {/* Plaka İşlemleri */}
        <SubmenuItem
          label="Plaka İşlemleri"
          onMouseEnter={() => setHoveredSubmenu("plaka")}
          onMouseLeave={() => setHoveredSubmenu(null)}
        >
          {hoveredSubmenu === "plaka" && (
            <Submenu
              items={[
                { label: "Plaka Geçmişi", onClick: () => { onOpenPlakaGecmisi?.(data); onClose(); } },
                { label: "Plaka Değiştir", onClick: () => { onOpenPlakaDegistir?.(data); onClose(); } },
              ]}
              onClose={onClose}
              onMouseEnter={() => setHoveredSubmenu("plaka")}
              onMouseLeave={() => setHoveredSubmenu(null)}
            />
          )}
        </SubmenuItem>

        <Divider />
        <SubmenuItem
          label="UETDS"
          onMouseEnter={() => setHoveredSubmenu("uetds")}
          onMouseLeave={() => setHoveredSubmenu(null)}
        >
          {hoveredSubmenu === "uetds" && (
            <Submenu
              items={[
                {
                  label: "Konum bildirimi ayarları",
                  onClick: () => {
                    onOpenUetdsKonumBildir?.(data);
                    onClose();
                  },
                },
                ...(userRole === "admin"
                  ? [
                      {
                        label: "Manuel arıza bildir",
                        onClick: () => {
                          onUetdsAriza?.(data);
                          onClose();
                        },
                      },
                    ]
                  : []),
              ]}
              onClose={onClose}
              onMouseEnter={() => setHoveredSubmenu("uetds")}
              onMouseLeave={() => setHoveredSubmenu(null)}
            />
          )}
        </SubmenuItem>

        <Divider />
        <MenuItem label="Aksesuar İşlemleri" onClick={() => { onOpenAksesuarIslemleri?.(data); onClose(); }} />
        <MenuItem label="Not Ekle" onClick={() => { onOpenNotEkle?.(data); onClose(); }} />
        <MenuItem label="İade Al" onClick={() => { onOpenIadeAl?.(data); onClose(); }} />
        {String(data?.satis_turu_raw || data?.satis_turu).toLowerCase() === "test" && (
          <MenuItem label="Montaj Yapıldı / Abonelik Başlat" onClick={() => { onOpenMontajBaslat?.(data); onClose(); }} />
        )}
      </Box>
    </>
  );
}

function SubmenuItem({ label, children, onMouseEnter, onMouseLeave }) {
  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      position="relative"
    >
      <Flex
        align="center"
        justify="space-between"
        px={3}
        py={1.5}
        cursor="pointer"
        borderRadius="md"
        mx={1}
        transition="all 0.15s ease"
        _hover={{
          bg: "gray.50",
          color: "gray.900",
          transform: "scale(1.02)"
        }}
      >
        <Text fontWeight="500" color="gray.600" fontSize="11px">
          {label}
        </Text>
        <Text color="gray.400" fontSize="10px" ml={2}>
          ▶
        </Text>
      </Flex>
      {children}
    </Box>
  );
}

function Submenu({ items, onClose, onMouseEnter, onMouseLeave }) {
  return (
    <Box
      position="absolute"
      top={0}
      left="100%"
      ml={2}
      bg="white"
      borderRadius="lg"
      shadow="0 4px 12px rgba(0, 0, 0, 0.15)"
      zIndex={2001}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      minW="160px"
      fontSize="11px"
      py={1}
      border="1px solid"
      borderColor="gray.200"
    >
      {items.map((item, index) => (
        <MenuItem
          key={index}
          label={item.label}
          onClick={item.onClick}
          isDestructive={item.isDestructive}
        />
      ))}
    </Box>
  );
}

function Divider() {
  return (
    <Box
      height="1px"
      bg="gray.200"
      mx={2}
      my={1}
    />
  );
}

function MenuItem({ label, onClick, isDestructive = false }) {
  return (
    <Flex
      align="center"
      px={3}
      py={1.5}
      cursor="pointer"
      borderRadius="md"
      mx={1}
      transition="all 0.15s ease"
      _hover={{
        bg: isDestructive ? "red.50" : "gray.50",
        color: isDestructive ? "red.600" : "gray.900",
        transform: "scale(1.02)"
      }}
      onClick={onClick}
    >
      <Text 
        fontWeight="500" 
        color={isDestructive ? "red.500" : "gray.600"}
        fontSize="11px"
      >
        {label}
      </Text>
    </Flex>
  );
}

export default AracContextMenu;